import type {
  CaseStudy,
  Insight,
  LeadRequest,
  LeadResponse,
  ServiceArea,
  SiteProfile
} from '../types/content';
import { clearToken, getToken } from './token';

export const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function send(path: string, init?: RequestInit): Promise<globalThis.Response> {
  const token = getToken();
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {})
    }
  });

  if (!response.ok) {
    // Utgången eller återkallad session – släng den så att admin visar
    // inloggningen i stället för att fortsätta anropa med en död token.
    if (response.status === 401 && token) {
      clearToken();
    }
    throw new ApiError(`Anropet till ${path} misslyckades.`, response.status);
  }

  return response;
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await send(path, init);
  return (await response.json()) as T;
}

export async function apiCommand(path: string, init?: RequestInit): Promise<void> {
  await send(path, init);
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return apiRequest<T>(path, init);
}

/** Publikt innehåll levereras på ett språk – svenska är fallback i API:et. */
function withLang(path: string, lang: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}lang=${encodeURIComponent(lang)}`;
}

export function fetchServices(lang: string, signal?: AbortSignal): Promise<ServiceArea[]> {
  return request<ServiceArea[]>(withLang('/services', lang), { signal });
}

export function fetchCases(lang: string, signal?: AbortSignal): Promise<CaseStudy[]> {
  return request<CaseStudy[]>(withLang('/cases', lang), { signal });
}

export function fetchInsights(lang: string, signal?: AbortSignal): Promise<Insight[]> {
  return request<Insight[]>(withLang('/insights', lang), { signal });
}

export function fetchInsight(slug: string, lang: string, signal?: AbortSignal): Promise<Insight> {
  return request<Insight>(withLang(`/insights/${encodeURIComponent(slug)}`, lang), { signal });
}

export function fetchSiteProfile(signal?: AbortSignal): Promise<SiteProfile> {
  return request<SiteProfile>('/site', { signal });
}

export function submitLead(payload: LeadRequest): Promise<LeadResponse> {
  return request<LeadResponse>('/leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
}
